/**
 * Where every piece of text on the reconstruction goes — and which ones have
 * no room.
 *
 * ## Why this pass sees all three kinds
 *
 * Its predecessor placed **one** kind, the measurement, and did that well:
 * measured in the browser against the recorded A-1 answer, no two figures
 * overlapped and none left the board. Three other texts were on the same
 * surface and none of them was in the check (review finding, 10.08.2026,
 * `viewBox` 539 × 380, all values from `getBBox()`):
 *
 * ```
 * Wärmepumpe Wiesbeet  interruption  x=430,0  w=130,1  → right edge 560,1 — 21 px off the board
 * ?                    mark          x=292,0  y=121,4  7,2 × 16,1  ┐ overlapping
 * 17.500 mm            dimension     x=266,3  y=109,9  58,6 × 13   ┘
 * ?                    mark          x= 55,0  y=121,4  7,2 × 16,1  ┐ overlapping
 * 1.280 mm             dimension     x= 32,6  y=109,9  52,1 × 13   ┘
 * ```
 *
 * The "?" is the colour-independent mark for a wall whose grating is
 * unconfirmed — the one thing on this drawing a reader without colour
 * perception has (spec §2.6). It sat on the figure Chiara is meant to check.
 * Making both illegible at once is worse than either alone, and it happened
 * for a structural reason: two of the three texts were drawn by the wall
 * component itself, where nothing can know what the neighbouring wall put in
 * the same place.
 *
 * So the pass owns **every** text on the board and applies the same two
 * conditions to each: **no overlap**, and **inside the board**. A fourth kind
 * cannot be added by drawing it somewhere — it has to come through here.
 *
 * ## Who gives way
 *
 * * The **mark** does not move. It is a claim about one particular wall, and a
 *   "?" that stepped aside would make that claim about the wall it stepped
 *   onto. It takes its place first and everything else avoids it.
 *
 *   **Known gap, left open on purpose:** two marks can therefore land on each
 *   other. Two adjacent walls whose grating is both unconfirmed and whose
 *   midpoints are less than about ten pixels apart produce two "?" in one
 *   place, and neither may give way. On A-1, A-8 and A-9 it does not occur —
 *   checked against the recorded runs — and no fix is worth trading the rule
 *   for: a mark that moved would be a false statement, while two marks on top
 *   of each other are an unreadable true one. Whoever hits it needs a
 *   different answer than "let it slide", most likely one mark for the pair
 *   with a leader to each wall. It is written here because an unwritten gap
 *   is a trap for the next person.
 * * The **measurement** and the **interruption's name** may step aside along
 *   their own wall and, failing that, away from it. Sliding uses the room
 *   along the facade, which is plentiful; pushing outwards uses the margin,
 *   which is the scarce direction. It is also what a draughtsman does — a
 *   dimension chain keeps its baseline and the cramped entries step aside
 *   with a leader.
 * * What fits nowhere is **reported, not dropped** (`crowded`). A label that
 *   is silently missing reads as a wall nobody measured, or a doorway nobody
 *   saw — the misreading this screen exists to prevent. Fourteen sections
 *   between 25 mm and 1.300 mm on one facade cannot all carry legible text at
 *   any board size; that is geometry. "No room here" and "not read" must
 *   still never look the same.
 *
 * Longest wall first, then drawing order. The long wall's text is the one
 * somebody checks first, and it is the one that reads worst at the end of a
 * leader; the short walls fill in around it.
 *
 * ## Why the board is measured in CSS pixels
 *
 * `drawingBoard` takes the pane's own width, so a box means the same thing on
 * a 1.600 px screen and on a phone. The board used to be a fixed 520-unit
 * `viewBox` stretched to the pane, which made every length here a lie by
 * whatever factor the browser happened to scale by — 11 px read 7,8 px just
 * above the two-column breakpoint and 5,6 px at 360 px viewport width.
 */
import type { ProjectionOptions, ScreenPoint } from "./projection";

/** The three sorts of text that stand on the drawing. */
export type LabelKind = "dimension" | "interruption" | "mark" | "landmark";

interface LabelMetrics {
  /** Type size in CSS pixels — mirrors the matching rule in
   * `OutlineReview.module.css`. The two have to agree: this module reserves
   * the room, that rule sets the glyphs. */
  fontPx: number;
  /** Advance of one character, CSS px. Set only for the fixed-pitch face,
   * where a character count is the whole answer; `null` means the width comes
   * from {@link emWidth}. */
  monoCharPx: number | null;
  /** Distance of the first anchor tried, measured from the wall. `0` means
   * "on the wall itself" — where an interruption's word belongs, because it
   * stands in the gap in the band (spec §2.6), and where the mark belongs,
   * because it is a statement about that wall. */
  firstOffsetPx: number;
  /** Whether it may leave the middle of its own wall at all. */
  movable: boolean;
  /** Whether it turns to run along a vertical wall. The mark does not: a
   * turned "?" is a worse glyph and it is one character wide either way. */
  turns: boolean;
}

/**
 * Mono at 11 px: 0,6 em, which is 6,6 px. The `−0,01 em` tracking of
 * `.dimensionLabel` is deliberately **not** subtracted — the browser measured
 * `17.500 mm` at 58,6 px over nine characters, i.e. 6,51 px each, so 6,5
 * would have been a tenth of a pixel short per character. A tenth over costs
 * nothing; a tenth under is an overlap nobody predicted. The UI face at 11 px
 * and 13 px is proportional and gets {@link emWidth}.
 */
const LABEL_METRICS: Record<LabelKind, LabelMetrics> = {
  dimension: { fontPx: 11, monoCharPx: 6.6, firstOffsetPx: 18, movable: true, turns: true },
  interruption: { fontPx: 11, monoCharPx: null, firstOffsetPx: 0, movable: true, turns: true },
  mark: { fontPx: 13, monoCharPx: null, firstOffsetPx: 0, movable: false, turns: false },
  // The name of a wall nobody measured (spec §6). Set off the wall like a
  // measurement and not on it like an interruption: it stands *instead of* a
  // figure, in the place a reader looks for one, and the wall under it is a
  // dashed line whose own ink a word sitting on it would break up.
  landmark: { fontPx: 11, monoCharPx: null, firstOffsetPx: 18, movable: true, turns: true },
};

/**
 * Height of a glyph box as a multiple of the type size.
 *
 * Measured, not guessed: `getBBox` on an SVG text node returns the face's own
 * line box. Measured in Chrome on 28.09.2026, after the switch to Roboto Mono
 * (fonts.css, UD-2) — 14,56 px for the 11 px mono figure, 13,05 px for a 10 px
 * one. 1,35 covers both (14,85 / 13,5) with air to spare; the earlier 1,25
 * was measured on the system mono this app rendered numbers in before that
 * switch.
 */
const LINE_BOX_RATIO = 1.35;

/** Clear air between two boxes, and between a box and the board edge. Also
 * the room the white halo behind each label needs: the widest is 4 px of
 * stroke, i.e. 2 px beyond the glyph. */
const LABEL_GAP_PX = 2;

/** Distance between rings. Must exceed the tallest movable box (≈14,9 px)
 * plus the gap, or two labels one ring apart still touch — at 13 px they
 * did, and the whole ladder collapsed onto itself. */
const RING_STEP_PX = 17;

/** How far out the search goes. The fourth ring is 69 px off the wall for a
 * measurement; beyond that a label is closer to a different wall than to its
 * own, and the leader stops being an explanation. */
const RING_COUNT = 4;

/** How many label-widths a label may slide along its wall, each direction. */
const SLIDE_STEPS = 3;

/* ── Width of proportional text, without a browser to ask ──────────────────
 *
 * The placement is a pure function, so the width of a word has to be computed
 * rather than measured. A flat average per character would have to be set for
 * the widest plausible word to stay safe, and would then declare "Sule"
 * unplaceable; four classes cost ten lines and keep both ends honest.
 *
 * These are **upper bounds on purpose.** Under-reserving puts one label on
 * another, which is the defect this pass exists to remove; over-reserving
 * sends a label one ring further out, or in the worst case into the list
 * under the drawing, where it is still readable. The values are set against
 * the widest system faces a browser may substitute (`system-ui` resolves
 * differently on every machine, and nothing here may depend on which one).
 * Checked against the one hard measurement available: `Wärmepumpe Wiesbeet`
 * at 11 px measured 130,1 px in the browser, and this table reserves 134,6.
 */
const EM_NARROW = 0.34;
const EM_WIDE = 0.95;
const EM_CAP = 0.72;
const EM_SPACE = 0.3;
const EM_DEFAULT = 0.6;

const NARROW_CHARS = "iíìîïjlıI|!.,;:'’‘`()[]{}";
const WIDE_CHARS = "mwMW@%—–";

function emWidth(char: string): number {
  if (char === " ") return EM_SPACE;
  if (NARROW_CHARS.includes(char)) return EM_NARROW;
  if (WIDE_CHARS.includes(char)) return EM_WIDE;
  // Catches every uppercase letter including Ä/Ö/Ü, without a second list.
  if (char !== char.toLowerCase()) return EM_CAP;
  if (char >= "0" && char <= "9") return EM_CAP;
  return EM_DEFAULT;
}

/** Length of the set text along its own baseline, CSS px. */
function textWidthPx(kind: LabelKind, text: string): number {
  const metrics = LABEL_METRICS[kind];
  if (metrics.monoCharPx !== null) return text.length * metrics.monoCharPx;
  let em = 0;
  for (const char of text) em += emWidth(char);
  return em * metrics.fontPx;
}

/** Height of the glyph box, CSS px. */
function lineHeightPx(kind: LabelKind): number {
  return Math.ceil(LABEL_METRICS[kind].fontPx * LINE_BOX_RATIO);
}

export interface LabelBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One wall's text, as the caller hands it over. A wall with nothing to say
 * sends no request — an undimensioned stretch (E-14) has no measurement to
 * print, and a confirmed wall has no mark. */
export interface LabelRequest {
  /** Opaque here, handed straight back on the placement. It exists so the
   * caller can say *which wall* a box belongs to without this module having
   * to know what a wall is. */
  id: string;
  kind: LabelKind;
  text: string;
  from: ScreenPoint;
  to: ScreenPoint;
}

export interface LabelPlacement {
  /** Position in the request list, so the caller can pair it back up. */
  index: number;
  /** The request's own {@link LabelRequest.id}, unchanged. */
  id: string;
  kind: LabelKind;
  text: string;
  /** Anchor for `text-anchor: middle` / `dominant-baseline: middle`. */
  x: number;
  y: number;
  /** −90 keeps a label upright along a vertical wall; 0 otherwise. */
  rotation: number;
  /** Which ring it landed on. 0 = the kind's own first offset. */
  tier: number;
  /** How far it slid along its wall, positive towards `to`. */
  shift: number;
  /** From the wall's midpoint to the label, drawn only where the label has
   * left its wall behind far enough to be read as a neighbour's. */
  leader: { x1: number; y1: number; x2: number; y2: number } | null;
  box: LabelBox;
}

export interface LabelLayout {
  /** In request order, so the DOM follows the walls. */
  placed: LabelPlacement[];
  /** Requests that found no free, fully visible spot. */
  crowded: number[];
}

/**
 * The drawing board for one reconstruction, in CSS pixels.
 *
 * The width is measured from the pane; the height is fixed. A board that kept
 * its aspect ratio would shrink in both directions on a phone, and height is
 * the one thing a phone still has — the page scrolls, the width does not.
 *
 * Padding is the room the labels live in. It follows the width so a narrow
 * board does not spend two thirds of itself on margin.
 */
export const BOARD_HEIGHT_PX = 380;

/** Used until the pane has been measured, and if it ever measures zero. */
export const BOARD_FALLBACK_WIDTH_PX = 520;

/** Smallest padding the board will accept. Also the guarantee that an
 * immovable mark is always fully visible: `projectRun` centres the shape, so
 * no wall midpoint is closer to an edge than this, and half a mark box plus
 * the gap is 10,5 px. */
const BOARD_MIN_PADDING_PX = 16;

export function drawingBoard(width: number): ProjectionOptions {
  const usable = Math.max(Math.round(width) || BOARD_FALLBACK_WIDTH_PX, 200);
  return {
    width: usable,
    height: BOARD_HEIGHT_PX,
    padding: Math.max(BOARD_MIN_PADDING_PX, Math.min(44, Math.round(usable / 6))),
  };
}

/** The box a label occupies at this anchor, upright or turned. */
export function labelBox(
  kind: LabelKind,
  text: string,
  x: number,
  y: number,
  rotation: number,
): LabelBox {
  const along = textWidthPx(kind, text);
  const across = lineHeightPx(kind);
  const width = rotation === 0 ? along : across;
  const height = rotation === 0 ? across : along;
  return { x: x - width / 2, y: y - height / 2, width, height };
}

function touches(a: LabelBox, b: LabelBox): boolean {
  return (
    a.x < b.x + b.width + LABEL_GAP_PX &&
    b.x < a.x + a.width + LABEL_GAP_PX &&
    a.y < b.y + b.height + LABEL_GAP_PX &&
    b.y < a.y + a.height + LABEL_GAP_PX
  );
}

function withinBoard(box: LabelBox, board: ProjectionOptions): boolean {
  return (
    box.x >= LABEL_GAP_PX &&
    box.y >= LABEL_GAP_PX &&
    box.x + box.width <= board.width - LABEL_GAP_PX &&
    box.y + box.height <= board.height - LABEL_GAP_PX
  );
}

interface WallFrame {
  midX: number;
  midY: number;
  /** Along the wall, from `from` towards `to`. */
  ux: number;
  uy: number;
  /** Away from the middle of the shape. */
  nx: number;
  ny: number;
  span: number;
  vertical: boolean;
}

/**
 * The wall's own coordinate frame: along it, and outwards from the shape.
 *
 * Same outward convention as `dimensionLine` — the normal is flipped to point
 * away from the centroid, so labels sit outside the house the way they do on
 * a workshop drawing.
 */
function frameOf(from: ScreenPoint, to: ScreenPoint, centroid: ScreenPoint): WallFrame {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const span = Math.hypot(dx, dy) || 1;
  const ux = dx / span;
  const uy = dy / span;
  let nx = -uy;
  let ny = ux;
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  if ((midX - centroid.x) * nx + (midY - centroid.y) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { midX, midY, ux, uy, nx, ny, span, vertical: Math.abs(dy) > Math.abs(dx) };
}

function rotationFor(kind: LabelKind, frame: WallFrame): number {
  return LABEL_METRICS[kind].turns && frame.vertical ? -90 : 0;
}

/**
 * Every spot this label may take, best first.
 *
 * Rings are the outer loop, slides the inner one: staying on the first ring
 * and stepping aside along the facade beats climbing away from it.
 */
function* candidates(
  kind: LabelKind,
  frame: WallFrame,
  text: string,
): Generator<{ x: number; y: number; tier: number; shift: number }> {
  const first = LABEL_METRICS[kind].firstOffsetPx;
  const stride = textWidthPx(kind, text) / 2 + 4;
  for (let tier = 0; tier < RING_COUNT; tier += 1) {
    const offset = first + tier * RING_STEP_PX;
    for (let step = 0; step <= SLIDE_STEPS; step += 1) {
      for (const shift of step === 0 ? [0] : [step * stride, -step * stride]) {
        yield {
          x: frame.midX + frame.nx * offset + frame.ux * shift,
          y: frame.midY + frame.ny * offset + frame.uy * shift,
          tier,
          shift,
        };
      }
    }
  }
}

/**
 * A leader answers one question: which wall is this?
 *
 * It is needed exactly when looking cannot answer it — when the label reaches
 * past the ends of its own wall, either because it slid or because the wall
 * is shorter than the text. A label sitting squarely outside its own long
 * wall needs none, however many rings out it sits, and a hairline there would
 * only add ink to a drawing that has enough. Nor does a label that never left
 * the wall's midpoint: the line would have zero length.
 */
function leaderFor(
  frame: WallFrame,
  along: number,
  spot: { x: number; y: number; shift: number },
): LabelPlacement["leader"] {
  const atMidpoint = spot.x === frame.midX && spot.y === frame.midY;
  const adrift = Math.abs(spot.shift) + along / 2 > frame.span / 2;
  if (atMidpoint || !adrift) return null;
  return { x1: frame.midX, y1: frame.midY, x2: spot.x, y2: spot.y };
}

/**
 * Place every label that fits, and name the ones that do not.
 *
 * Deterministic: the same walls on the same board always produce the same
 * layout, because the order is fixed (immovable first, then longest wall
 * first with ties by request order) and the candidate sequence is fixed.
 */
export function placeLabels(
  requests: LabelRequest[],
  centroid: ScreenPoint,
  board: ProjectionOptions,
  reserved: readonly LabelBox[] = [],
): LabelLayout {
  const wanted = requests
    .map((request, index) => ({ request, index, frame: frameOf(request.from, request.to, centroid) }))
    .filter((entry) => entry.request.text !== "");

  // Boxes the caller has already spoken for, before anything of ours is
  // placed. A dimension figure is drawn *outside* this pass — it has one fixed
  // place beside its wall and no second-best — so this module cannot see it,
  // and a landmark on the same wall used to be set straight on top of it
  // (measured in the browser, 15.08.2026). Handing the figures in as occupied
  // space is what lets the one pass that can see all the texts also see the
  // one text it does not place.
  const taken: LabelBox[] = [...reserved];
  const placed: LabelPlacement[] = [];
  const crowded: number[] = [];

  // Whatever may not move claims its place first, unconditionally. A mark is
  // a statement about one wall; there is no second-best position for it, and
  // no board on which it is invisible either — see BOARD_MIN_PADDING_PX.
  for (const { request, index, frame } of wanted) {
    if (LABEL_METRICS[request.kind].movable) continue;
    const rotation = rotationFor(request.kind, frame);
    const box = labelBox(request.kind, request.text, frame.midX, frame.midY, rotation);
    taken.push(box);
    placed.push({
      index,
      id: request.id,
      kind: request.kind,
      text: request.text,
      x: frame.midX,
      y: frame.midY,
      rotation,
      tier: 0,
      shift: 0,
      leader: null,
      box,
    });
  }

  const movable = wanted
    .filter((entry) => LABEL_METRICS[entry.request.kind].movable)
    .sort((a, b) => b.frame.span - a.frame.span || a.index - b.index);

  for (const { request, index, frame } of movable) {
    const rotation = rotationFor(request.kind, frame);
    let found: LabelPlacement | null = null;

    for (const spot of candidates(request.kind, frame, request.text)) {
      const box = labelBox(request.kind, request.text, spot.x, spot.y, rotation);
      if (!withinBoard(box, board)) continue;
      if (taken.some((other) => touches(other, box))) continue;
      found = {
        index,
        id: request.id,
        kind: request.kind,
        text: request.text,
        x: spot.x,
        y: spot.y,
        rotation,
        tier: spot.tier,
        shift: spot.shift,
        leader: leaderFor(frame, textWidthPx(request.kind, request.text), spot),
        box,
      };
      break;
    }

    if (found === null) {
      crowded.push(index);
      continue;
    }
    taken.push(found.box);
    placed.push(found);
  }

  placed.sort((a, b) => a.index - b.index);
  crowded.sort((a, b) => a - b);
  return { placed, crowded };
}
